import { useEffect, useMemo, useRef, useState } from "react";
import { useLazyGetComplexityScoresQuery } from "features/api/customQuotesApi.ts";
import { models } from "types/api/viewModels.ts";
import { mergeComplexityScoresBySku } from "pages/CustomQuote/AddQuotePage/utils/cqLineItemUtils.ts";

interface UseCqComplexityScoresProps {
	cqMods?: models["CqModViewModel"][] | null;
}

export const useCqComplexityScores = ({ cqMods }: UseCqComplexityScoresProps): number => {
	const [cqComplexityScoreBySku, setCqComplexityScoreBySku] = useState<Record<string, number>>({});
	const [getComplexityScores] = useLazyGetComplexityScoresQuery();

	const cqModificationSkus = useMemo(() => {
		const uniqueSkus = new Set<string>();
		cqMods?.forEach((modification) => {
			if (modification?.sku) {
				uniqueSkus.add(modification.sku);
			}
		});
		return Array.from(uniqueSkus);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		// eslint-disable-next-line react-hooks/exhaustive-deps
		cqMods
			?.map((m) => m?.sku)
			.filter(Boolean)
			.sort()
			.join(",")
	]);

	const fetchedSkusRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		if (cqModificationSkus.length === 0) return;

		const missingSkus = cqModificationSkus.filter((sku) => !fetchedSkusRef.current.has(sku));
		if (missingSkus.length === 0) return;

		missingSkus.forEach((sku) => fetchedSkusRef.current.add(sku));

		let isCancelled = false;

		const fetchComplexityScores = async () => {
			const scoreEntries = await Promise.all(
				missingSkus.map(async (sku) => {
					try {
						const response = await getComplexityScores(sku).unwrap();
						const complexityScore =
							typeof response?.complexityScore === "number" && Number.isFinite(response.complexityScore)
								? response.complexityScore
								: 0;
						return [sku, complexityScore] as const;
					} catch {
						return [sku, 0] as const;
					}
				})
			);

			if (isCancelled) {
				return;
			}

			setCqComplexityScoreBySku((prev) => mergeComplexityScoresBySku(prev, scoreEntries));
		};

		void fetchComplexityScores();

		return () => {
			isCancelled = true;
		};
	}, [cqModificationSkus, getComplexityScores]);

	const total = useMemo(
		() =>
			cqMods?.reduce((sum, mod) => {
				if (!mod?.sku) return sum;
				return sum + (cqComplexityScoreBySku[mod.sku] ?? 0);
			}, 0) ?? 0,
		[cqComplexityScoreBySku, cqMods]
	);

	return total;
};

export default useCqComplexityScores;
